import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Plus, Trash2, Edit, X } from "lucide-react";

const ROLES = [
  { v: "super_admin", l: "Super-admin" },
  { v: "admin", l: "Administrateur" },
  { v: "editor", l: "Éditeur" },
  { v: "sales", l: "Commercial" },
  { v: "viewer", l: "Lecture seule" },
];

const ROLE_LABEL = Object.fromEntries(ROLES.map(r => [r.v, r.l]));

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null); // { user? }

  const load = () => api.get("/admin/users").then(r => setUsers(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!confirm("Supprimer cet utilisateur ?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-12">
        <Link to="/admin" className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-6 inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Retour</Link>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Utilisateurs & rôles</h1>
          <button onClick={() => setModal({})} data-testid="add-user-btn" className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Plus className="w-4 h-4"/>Nouvel utilisateur</button>
        </div>

        <div className="bg-white border border-slate-900/15 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr><Th>Nom</Th><Th>Email</Th><Th>Rôle</Th><Th>Créé le</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-slate-900/10" data-testid={`row-user-${u.id}`}>
                  <td className="px-4 py-3 font-semibold">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3"><span className="tag">{ROLE_LABEL[u.role] || u.role}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{u.created_at?.slice(0,10)}</td>
                  <td className="px-4 py-3 text-right flex justify-end gap-2">
                    <button onClick={() => setModal({ user: u })} className="p-2 border border-slate-900 hover:bg-slate-900 hover:text-white" data-testid={`edit-user-${u.id}`}><Edit className="w-4 h-4"/></button>
                    <button onClick={() => remove(u.id)} className="p-2 border border-slate-900 hover:bg-red-600 hover:text-white hover:border-red-600" data-testid={`del-user-${u.id}`}><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Role descriptions */}
        <div className="mt-8 grid md:grid-cols-2 gap-3 text-sm">
          {[
            ["Super-admin", "Accès total + gestion des utilisateurs"],
            ["Administrateur", "Gestion complète sauf utilisateurs"],
            ["Éditeur", "Pages, blog, médias, contenu, témoignages, partenaires"],
            ["Commercial", "Réservations + demandes contact + consultation des locaux"],
            ["Lecture seule", "Consultation uniquement (read-only)"],
          ].map(([k, v]) => (
            <div key={k} className="bg-white border border-slate-900/15 p-4">
              <div className="font-display font-bold text-sm">{k}</div>
              <div className="text-xs text-slate-600 mt-1">{v}</div>
            </div>
          ))}
        </div>
      </section>
      <Footer />
      {modal && <UserModal user={modal.user} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function UserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState(user ? { name: user.name, email: user.email, role: user.role, password: "" } : { name: "", email: "", role: "editor", password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (user) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${user.id}`, payload);
        toast.success("Utilisateur mis à jour");
      } else {
        await api.post("/admin/users", form);
        toast.success("Utilisateur créé");
      }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white border border-slate-900 w-full max-w-md p-8 space-y-4" data-testid="user-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl">{user ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Nom *</span><input required value={form.name} onChange={set("name")} className="input" data-testid="user-name"/></label>
        {!user && <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Email *</span><input required type="email" value={form.email} onChange={set("email")} className="input" data-testid="user-email"/></label>}
        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Mot de passe {user && "(laisser vide pour ne pas changer)"}</span><input type="password" required={!user} value={form.password} onChange={set("password")} className="input" data-testid="user-password"/></label>
        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Rôle</span>
          <select value={form.role} onChange={set("role")} className="input" data-testid="user-role">{ROLES.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}</select>
        </label>
        <button disabled={saving} type="submit" data-testid="user-submit" className="btn-brutal btn-brutal-solar w-full px-6 py-3 font-bold uppercase tracking-wider text-sm">{saving ? "..." : "Enregistrer"}</button>
      </form>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-widest ${className}`}>{children}</th>
);
